import { forEachPathItemOperation, getPathItemOperation } from '../helpers/for-each-path-item-operation.js';
import { generateUniqueValue } from '../helpers/generate-unique-value.js';
import { getResolvedRef } from '../helpers/get-resolved-ref.js';
import { isNonOptionalSecurityRequirement } from '../helpers/is-non-optional-security-requirement.js';
import { mergeObjects } from '../helpers/merge-object.js';
import { unpackProxyObject } from '../helpers/unpack-proxy.js';
import { getSelectedSecurity } from '../request-example/context/security/get-selected-security.js';
import { isAsyncApiDocument, isOpenApiDocument } from '../schemas/type-guards.js';
/**
 * The auth-store key for a document's selection and secret state.
 *
 * Both OpenAPI and AsyncAPI documents persist it on `x-scalar-navigation.name`, so auth selection
 * and credential entry work for either spec. Returns `undefined` for anything else (or a document
 * without a navigation name), in which case the caller does nothing.
 */
const getAuthDocumentName = (document) => {
    if (isOpenApiDocument(document) || isAsyncApiDocument(document)) {
        return document['x-scalar-navigation']?.name || undefined;
    }
    return undefined;
};
/**
 * The document's `components.securitySchemes`, resolving the AsyncAPI `components` `$ref` wrapper.
 * Used when building a fallback selection, so scope selection works for both spec types.
 */
const getDocumentSecuritySchemes = (document) => {
    if (isOpenApiDocument(document)) {
        return (document.components?.securitySchemes ?? {});
    }
    if (isAsyncApiDocument(document)) {
        const components = document.components ? getResolvedRef(document.components) : undefined;
        return (components?.securitySchemes ?? {});
    }
    return {};
};
/**
 * The key an OAuth2 flow stores its scope map under. OpenAPI uses `scopes`; AsyncAPI uses
 * `availableScopes` (which `mergeSecurity` renames to `scopes` for rendering), so scope edits
 * must target the right key to round-trip.
 */
const getFlowScopeKey = (document) => isAsyncApiDocument(document) ? 'availableScopes' : 'scopes';
/**
 * Updates the selected security schemes for either the entire document or a specific operation.
 * - Adds newly created security schemes (if any) to the workspace document's components.
 * - Ensures that each new scheme name is unique within the document by using `generateUniqueValue`.
 * - Updates the `x-scalar-selected-security` property on the target (document or operation) to reflect the new set of selected security schemes.
 * - Corrects and maintains the selected index so it points to a valid security scheme.
 *
 * @param document - The workspace OpenAPI document to mutate (can be null, in which case nothing happens)
 * @param selectedSecuritySchemes - The current list of selected security scheme objects
 * @param create - Array of new schemes to create, each with a name and a scheme definition
 * @param meta - Location to update: whole document or a specific operation (`{ type: 'document' }` or `{ type: 'operation', path, method }`)
 *
 * Example usage:
 * ```
 * updateSelectedSecuritySchemes({
 *   document,
 *   selectedSecuritySchemes: [{ bearerAuth: [] }],
 *   create: [
 *     { name: 'ApiKeyAuth', scheme: { type: 'apiKey', in: 'header', name: 'X-API-Key' } }
 *   ],
 *   meta: { type: 'document' }
 * })
 * ```
 */
export const updateSelectedSecuritySchemes = async (store, document, { selectedRequirements, newSchemes, meta }) => {
    const documentName = getAuthDocumentName(document);
    if (!documentName) {
        return;
    }
    // Helper to get the target (whole document or a specific operation)
    const getTarget = () => {
        if (meta.type === 'document') {
            return store?.auth.getAuthSelectedSchemas({ type: 'document', documentName });
        }
        return store?.auth.getAuthSelectedSchemas({ type: 'operation', documentName, path: meta.path, method: meta.method });
    };
    // New scheme definitions can only be materialised into an OpenAPI `components` block. AsyncAPI
    // document-level auth only ever selects existing schemes, so there is nothing to create there.
    const createdSchemes = [];
    if (isOpenApiDocument(document)) {
        const createdSecurityRequirements = await Promise.all(newSchemes.map(async (newScheme) => {
            const uniqueSchemeName = await generateUniqueValue({
                defaultValue: newScheme.name,
                validation: (value) => !document.components?.securitySchemes?.[value],
                maxRetries: 100,
            });
            if (!uniqueSchemeName) {
                return;
            }
            // Ensure components and securitySchemes exist
            if (!document.components) {
                document.components = {};
            }
            if (!document.components.securitySchemes) {
                document.components.securitySchemes = {};
            }
            // Add the new security scheme definition
            document.components.securitySchemes[uniqueSchemeName] = newScheme.scheme;
            // Return an OpenAPI Security Requirement Object for this new scheme (empty scope array)
            return {
                [uniqueSchemeName]: [],
            };
        }));
        createdSchemes.push(...createdSecurityRequirements.filter(Boolean));
    }
    const target = getTarget();
    const newSelectedSecuritySchemes = [...selectedRequirements, ...createdSchemes];
    const getSelectedIndex = () => {
        if (!target?.selectedIndex) {
            return 0;
        }
        if (target.selectedIndex >= newSelectedSecuritySchemes.length) {
            return newSelectedSecuritySchemes.length - 1;
        }
        return target.selectedIndex;
    };
    // if (payload. === 'document') {
    if (meta.type === 'document') {
        return store?.auth.setAuthSelectedSchemas({ type: 'document', documentName }, { selectedIndex: getSelectedIndex(), selectedSchemes: newSelectedSecuritySchemes });
    }
    return store?.auth.setAuthSelectedSchemas({ type: 'operation', documentName, path: meta.path, method: meta.method }, { selectedIndex: getSelectedIndex(), selectedSchemes: newSelectedSecuritySchemes });
};
/**
 * Clears the selected security schemes from the workspace store for a document or operation.
 * This function will remove any selection state related to security (auth) for either the entire document
 * or for a specific operation if meta.type is 'operation'.
 * If the document name cannot be determined, nothing happens.
 */
const clearSelectedSecuritySchemes = (store, document, { meta }) => {
    const documentName = getAuthDocumentName(document);
    if (!documentName) {
        return;
    }
    if (meta.type === 'document') {
        return store?.auth.clearAuthSelectedSchemas({ type: 'document', documentName });
    }
    return store?.auth.clearAuthSelectedSchemas({ type: 'operation', documentName, path: meta.path, method: meta.method });
};
/**
 * Updates a security scheme in the OpenAPI document's components object.
 * Handles updates for HTTP, API Key, and OAuth2 types, saving secret information and configuration for UI-auth flows.
 *
 * @param document - The OpenAPI workspace document (can be null)
 * @param data - The update information, including type and payload
 * @param name - The name of the security scheme in document.components.securitySchemes
 *
 * Example usage:
 *
 * updateSecurityScheme({
 *   document,
 *   data: {
 *     type: 'http',
 *     payload: {
 *       username: 'user123',
 *       password: 'pw123',
 *       token: 'tokenval'
 *     }
 *   },
 *   name: 'MyHttpAuth',
 * })
 */
export const updateSecurityScheme = (document, { payload, name }) => {
    if (!isOpenApiDocument(document) && !isAsyncApiDocument(document)) {
        return;
    }
    const target = getResolvedRef(getDocumentSecuritySchemes(document)[name]);
    if (!target) {
        console.error(`Security scheme ${name} not found`);
        return;
    }
    // AsyncAPI's `httpApiKey` is presented as `apiKey` in the UI, so treat that as an equivalent
    // update. Handle HTTP (basic, bearer, etc.) and the shared types the same way. The stored type is
    // read as a string because AsyncAPI broker types are outside the OpenAPI `SecuritySchemeObject` union.
    const storedType = target.type;
    const isEquivalentType = storedType === payload.type || (storedType === 'httpApiKey' && payload.type === 'apiKey');
    if (isEquivalentType) {
        // Preserve the document's original spec type — never let the UI's normalized type overwrite it.
        const originalType = target.type;
        mergeObjects(target, payload);
        target.type = originalType;
    }
    return target;
};
const updateSecuritySchemeSecrets = (store, document, { payload, name, overwrite = false }) => {
    const documentName = getAuthDocumentName(document);
    if (!documentName) {
        return;
    }
    // If we want to remove properties then we should set replace to true
    if (overwrite) {
        store?.auth.setAuthSecrets(documentName, name, payload);
        return;
    }
    const auth = store?.auth.getAuthSecrets(documentName, name);
    const result = mergeObjects(unpackProxyObject(auth, { depth: 1 }) ?? {}, payload);
    store?.auth.setAuthSecrets(documentName, name, result);
};
const clearSecuritySchemeSecrets = (store, document, { name }) => {
    const documentName = getAuthDocumentName(document);
    if (!documentName) {
        return;
    }
    store?.auth.clearAuthSecrets(documentName, name);
};
/**
 * Sets the selected authentication tab (scheme) index for the given OpenAPI document or operation.
 * - When on the document level, updates the 'selectedIndex' on the document's x-scalar-selected-security extension.
 * - When on an operation (endpoint) level, updates the 'selectedIndex' for that operation's x-scalar-selected-security.
 *
 * Also initializes the x-scalar-selected-security extension if it does not exist.
 *
 * @param document The OpenAPI document object (may be null)
 * @param index The index to set as selected
 * @param meta Context where the selection applies ('document' or specific operation)
 *
 * @example
 * // Document-level tab selection
 * updateSelectedAuthTab({
 *   document,
 *   index: 1,
 *   meta: { type: 'document' }
 * });
 *
 * // Operation-level tab selection (e.g., GET /pets)
 * updateSelectedAuthTab({
 *   document,
 *   index: 0,
 *   meta: { type: 'operation', path: '/pets', method: 'get' }
 * });
 */
export const updateSelectedAuthTab = (store, document, { index, meta }) => {
    const documentName = getAuthDocumentName(document);
    if (!documentName) {
        return;
    }
    // Operation-level auth only applies to OpenAPI documents; ensure the path/method exists.
    if (meta.type === 'operation') {
        if (!isOpenApiDocument(document) || getPathItemOperation(document.paths?.[meta.path], meta.method) === undefined) {
            return;
        }
    }
    // Determine the target object for setting the auth tab index:
    // - Document/root level
    // - Operation/endpoint level (if meta specifies operation)
    const getTarget = () => {
        if (meta.type === 'document') {
            return store?.auth.getAuthSelectedSchemas({ type: 'document', documentName });
        }
        return store?.auth.getAuthSelectedSchemas({ type: 'operation', documentName, path: meta.path, method: meta.method });
    };
    const target = getTarget();
    if (!target) {
        if (meta.type === 'document') {
            return store?.auth.setAuthSelectedSchemas({ type: 'document', documentName }, { selectedIndex: index, selectedSchemes: [] });
        }
        return store?.auth.setAuthSelectedSchemas({ type: 'operation', documentName, path: meta.path, method: meta.method }, { selectedIndex: index, selectedSchemes: [] });
    }
    store?.auth.setAuthSelectedSchemas(meta.type === 'document'
        ? { type: 'document', documentName }
        : { type: 'operation', documentName, path: meta.path, method: meta.method }, {
        selectedIndex: index,
        selectedSchemes: unpackProxyObject(target.selectedSchemes, { depth: null }) ?? [],
    });
};
/**
 * Returns whether `id` lists the same scheme names as `requirement` (OpenAPI security requirement keys),
 * ignoring key order. UI payloads use `Object.keys(selectedSecuritySchemas)` which follows insertion order;
 * stored copies may serialize with a different order.
 */
const securityRequirementIdsMatch = (requirement, id) => {
    const sortedRequirementKeys = [...Object.keys(requirement)].sort((a, b) => a.localeCompare(b));
    const sortedId = [...id].sort((a, b) => a.localeCompare(b));
    return JSON.stringify(sortedRequirementKeys) === JSON.stringify(sortedId);
};
/**
 * Updates the scopes for a specific security requirement in the selected security schemes of
 * a document or operation. This mutator only touches selection state; managing the scope
 * definitions on the OAuth flow is handled by `upsertScope` and `deleteScope`.
 *
 * @param document - The OpenAPI WorkspaceDocument to update.
 * @param id - An array of scheme names that uniquely identifies the target security requirement.
 *             For example: ['OAuth', 'ApiKeyAuth']
 * @param name - The security scheme name to update scopes for (e.g., 'OAuth').
 * @param scopes - The new list of scopes to set. For example: ['read:pets', 'write:pets']
 * @param meta - The context specifying whether the update is at the document-level or operation-level.
 *
 * Example usage:
 * ```ts
 * // Suppose your document (or operation) x-scalar-selected-security looks like:
 * // "x-scalar-selected-security": {
 * //   selectedIndex: 0,
 * //   selectedSchemes: [
 * //     { "OAuth": ["read:pets"] },
 * //     { "ApiKeyAuth": [] }
 * //   ]
 * // }
 *
 * updateSelectedScopes({
 *   document,
 *   id: ["OAuth"],     // identifies the scheme object: { "OAuth": [...] }
 *   name: "OAuth",     // scheme name to update within this security requirement
 *   scopes: ["write:pets"], // new scopes array
 *   meta: { type: "document" }
 * })
 * // After, the first scheme becomes: { "OAuth": ["write:pets"] }
 * ```
 */
export const updateSelectedScopes = (store, document, { id, name, scopes, scope, selected, meta }) => {
    const documentName = getAuthDocumentName(document);
    if (!documentName) {
        return;
    }
    // Determine the target object (document or the operation)
    const getTarget = () => {
        if (meta.type === 'document') {
            return store?.auth.getAuthSelectedSchemas({ type: 'document', documentName });
        }
        return store?.auth.getAuthSelectedSchemas({ type: 'operation', documentName, path: meta.path, method: meta.method });
    };
    // Resolve the target lazily: only build a fallback selection when the store has none.
    //
    // We pass `[]` for security requirements because a `preferredSecurityScheme` is always
    // supplied (derived from `id`), which makes `getSelectedSecurity` build the requirement
    // from that scheme alone and never read the requirements array.
    const target = getTarget() ??
        getSelectedSecurity(undefined, undefined, [], getDocumentSecuritySchemes(document), id.length === 1 ? id[0] : id);
    const nextSelectedSchemes = unpackProxyObject(target.selectedSchemes, { depth: 1 }) ?? [];
    // Match the security requirement by scheme key names (order-insensitive: Object.keys order
    // can differ between the store copy and the UI payload for the same requirement object).
    const nextScheme = nextSelectedSchemes.find((candidate) => securityRequirementIdsMatch(candidate, id));
    if (!isNonOptionalSecurityRequirement(nextScheme)) {
        return;
    }
    // Resolve the next scope list. A single-scope toggle (`scope` + `selected`) is applied against the
    // value currently in the store, which is what keeps rapid successive clicks from overwriting each
    // other with a list the component computed from a possibly-stale prop. Bulk actions pass an absolute
    // `scopes` list instead. A payload carrying neither is ignored rather than silently clearing.
    const resolveNextScopes = () => {
        if (scope !== undefined && selected !== undefined) {
            const currentScopes = Array.isArray(nextScheme[name]) ? nextScheme[name] : [];
            return selected
                ? Array.from(new Set([...currentScopes, scope]))
                : currentScopes.filter((current) => current !== scope);
        }
        return scopes;
    };
    const nextScopes = resolveNextScopes();
    if (nextScopes === undefined) {
        return;
    }
    nextScheme[name] = nextScopes;
    store?.auth.setAuthSelectedSchemas(meta.type === 'document'
        ? { type: 'document', documentName }
        : { type: 'operation', documentName, path: meta.path, method: meta.method }, { selectedIndex: target.selectedIndex, selectedSchemes: nextSelectedSchemes });
};
/**
 * Resolves the OAuth flow on a security scheme by name + flow type.
 * Returns `null` when the scheme or flow cannot be found, or the scheme is not an OAuth2 / OpenID Connect scheme.
 */
const resolveOAuthFlow = (document, name, flowType) => {
    const securityScheme = getResolvedRef(getDocumentSecuritySchemes(document)[name]);
    if (!securityScheme) {
        return null;
    }
    if (securityScheme.type !== 'oauth2' && securityScheme.type !== 'openIdConnect') {
        return null;
    }
    const flow = securityScheme.flows?.[flowType];
    return flow ? getResolvedRef(flow) : null;
};
/**
 * Walks every selection container that lives under a document (the document-level
 * `x-scalar-selected-security` plus the equivalent on every path / method) and invokes
 * `transform` on a plain copy of `selectedSchemes`, then writes back through
 * `setAuthSelectedSchemas` so persistence hooks run.
 */
const walkSelectedSchemes = (store, document, transform) => {
    const documentName = getAuthDocumentName(document);
    if (!store || !documentName) {
        return;
    }
    const apply = (payload) => {
        const target = store.auth.getAuthSelectedSchemas(payload);
        if (!target) {
            return;
        }
        const nextSchemes = unpackProxyObject(target.selectedSchemes, { depth: 1 }) ?? [];
        transform(nextSchemes);
        store.auth.setAuthSelectedSchemas(payload, {
            selectedIndex: target.selectedIndex,
            selectedSchemes: nextSchemes,
        });
    };
    apply({ type: 'document', documentName });
    // Operation-level selections only exist for OpenAPI documents (path / method).
    if (isOpenApiDocument(document)) {
        Object.entries(document.paths ?? {}).forEach(([path, pathItemRef]) => {
            forEachPathItemOperation(pathItemRef, (method) => {
                apply({ type: 'operation', documentName, path, method });
            });
        });
    }
};
/**
 * Adds a new scope to an OAuth flow, or renames / updates the description of an existing one.
 *
 * When `oldScope` differs from `scope`, this mutator also rewrites every selection entry
 * that references the matching security scheme so consumers do not need a follow-up
 * `auth:update:selected-scopes`.
 *
 * When `enable` is true, the resulting `scope` is additionally appended to every selection
 * requirement that already references this security scheme, enabling an "add and select"
 * flow without a separate selection mutation.
 */
export const upsertScope = (store, document, { name, flowType, scope, description, oldScope, enable }) => {
    if (!isOpenApiDocument(document) && !isAsyncApiDocument(document)) {
        return;
    }
    const flow = resolveOAuthFlow(document, name, flowType);
    if (!flow) {
        return;
    }
    const scopeKey = getFlowScopeKey(document);
    if (!flow[scopeKey]) {
        flow[scopeKey] = {};
    }
    const scopes = flow[scopeKey];
    const isRename = Boolean(oldScope) && oldScope !== scope;
    // Rename: drop the previous key so iteration order stays predictable.
    if (isRename) {
        if (!(oldScope in scopes)) {
            return;
        }
        delete scopes[oldScope];
    }
    scopes[scope] = description;
    if (!isRename && !enable) {
        return;
    }
    // Mirror the rename and/or apply `enable` across selection entries that reference this scheme.
    walkSelectedSchemes(store, document, (selectedSchemes) => {
        selectedSchemes.forEach((requirement) => {
            if (!isNonOptionalSecurityRequirement(requirement)) {
                return;
            }
            const scopes = requirement[name];
            if (!Array.isArray(scopes)) {
                return;
            }
            let nextScopes = scopes;
            // Rewrite the old key in place when this requirement had the renamed scope selected.
            if (isRename && nextScopes.includes(oldScope)) {
                nextScopes = nextScopes.map((current) => (current === oldScope ? scope : current));
            }
            // Append the resulting scope when the caller asked for "add and select".
            if (enable && !nextScopes.includes(scope)) {
                nextScopes = [...nextScopes, scope];
            }
            if (nextScopes !== scopes) {
                requirement[name] = nextScopes;
            }
        });
    });
};
/**
 * Removes a scope from an OAuth flow and strips it from any selection state that references
 * the matching security scheme.
 */
export const deleteScope = (store, document, { name, flowType, scope }) => {
    if (!isOpenApiDocument(document) && !isAsyncApiDocument(document)) {
        return;
    }
    const flow = resolveOAuthFlow(document, name, flowType);
    const scopes = flow?.[getFlowScopeKey(document)];
    if (!scopes) {
        return;
    }
    delete scopes[scope];
    walkSelectedSchemes(store, document, (selectedSchemes) => {
        selectedSchemes.forEach((requirement) => {
            if (!isNonOptionalSecurityRequirement(requirement)) {
                return;
            }
            const scopes = requirement[name];
            if (!Array.isArray(scopes) || !scopes.includes(scope)) {
                return;
            }
            requirement[name] = scopes.filter((current) => current !== scope);
        });
    });
};
/**
 * Deletes one or more security schemes from an OpenAPI WorkspaceDocument,
 * and removes all references to those schemes from selected security, document-level security,
 * and operation-level security/selected security (e.g., on paths).
 *
 * Example usage:
 *
 * ```ts
 * deleteSecurityScheme({
 *   document,                                // The OpenAPI document to update
 *   names: ['ApiKeyAuth', 'BearerAuth'],     // The names of security schemes you want to delete
 * });
 * ```
 *
 * After running this function:
 * - The named security schemes are removed from the components.securitySchemes section.
 * - All document-level and operation-level security entries referencing those schemes are removed.
 * - Any extended x-scalar-selected-security references to those schemes are also removed.
 */
export const deleteSecurityScheme = (store, document, { names }) => {
    if (!isOpenApiDocument(document)) {
        // Early exit if there is no document to modify
        return;
    }
    const documentName = document['x-scalar-navigation']?.name;
    if (!documentName) {
        return;
    }
    // Get the mutable reference to securitySchemes in components (may be a proxy/resolved reference)
    const target = getResolvedRef(document.components?.securitySchemes);
    if (!target) {
        // If there are no security schemes to delete from, return early
        return;
    }
    // Remove each named security scheme from the components.securitySchemes object
    names.forEach((name) => {
        delete target[name];
    });
    const clampIndex = (index, length) => {
        return Math.max(0, Math.min(index, length - 1));
    };
    // Function to remove any security requirement objects that reference given scheme names.
    const filterSecuritySchemes = (_schemes) => {
        const schemes = unpackProxyObject(_schemes, { depth: 1 }) ?? [];
        // Remove schemes whose key is included in the `names` to be deleted.
        return schemes.filter((scheme) => !names.some((name) => Object.keys(scheme).includes(name)));
    };
    const documentSelectedSecurity = store?.auth.getAuthSelectedSchemas({ type: 'document', documentName });
    // -- Remove from document-level `x-scalar-selected-security` extension, if present
    if (documentSelectedSecurity) {
        const filtered = filterSecuritySchemes(documentSelectedSecurity.selectedSchemes);
        store?.auth.setAuthSelectedSchemas({ type: 'document', documentName }, {
            selectedIndex: clampIndex(documentSelectedSecurity.selectedIndex, filtered.length),
            selectedSchemes: filtered,
        });
    }
    // -- Remove from document-level `security` property, if present
    if (document['security']) {
        document['security'] = filterSecuritySchemes(document['security']);
    }
    // -- For each path and operation, remove deleted security schemes from operation-level security and custom extension
    Object.entries(document.paths ?? {}).forEach(([path, pathItemRef]) => {
        forEachPathItemOperation(pathItemRef, (method, operationRef) => {
            // Get mutable reference for the operation (could resolve $ref proxies)
            const resolvedOperation = getResolvedRef(operationRef);
            // Remove from operation-level security array
            if ('security' in resolvedOperation && resolvedOperation['security']) {
                resolvedOperation['security'] = filterSecuritySchemes(resolvedOperation['security']);
            }
            const operationSelectedSecurity = store?.auth.getAuthSelectedSchemas({
                type: 'operation',
                documentName,
                path,
                method,
            });
            if (operationSelectedSecurity) {
                const filtered = filterSecuritySchemes(operationSelectedSecurity.selectedSchemes);
                store?.auth.setAuthSelectedSchemas({ type: 'operation', documentName, path, method }, {
                    selectedIndex: clampIndex(operationSelectedSecurity.selectedIndex, filtered.length),
                    selectedSchemes: filtered,
                });
            }
        });
    });
};
export const authMutatorsFactory = ({ document, store, }) => {
    return {
        updateSelectedSecuritySchemes: (payload) => updateSelectedSecuritySchemes(store, document, payload),
        clearSelectedSecuritySchemes: (payload) => clearSelectedSecuritySchemes(store, document, payload),
        updateSecurityScheme: (payload) => updateSecurityScheme(document, payload),
        updateSecuritySchemeSecrets: (payload) => updateSecuritySchemeSecrets(store, document, payload),
        clearSecuritySchemeSecrets: (payload) => clearSecuritySchemeSecrets(store, document, payload),
        updateSelectedAuthTab: (payload) => updateSelectedAuthTab(store, document, payload),
        updateSelectedScopes: (payload) => updateSelectedScopes(store, document, payload),
        upsertScope: (payload) => upsertScope(store, document, payload),
        deleteScope: (payload) => deleteScope(store, document, payload),
        deleteSecurityScheme: (payload) => deleteSecurityScheme(store, document, payload),
    };
};
